import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import './Home.css';

function HeroClock() {
  return (
    <svg width="72" height="72" viewBox="0 0 64 64" fill="none" aria-hidden="true" className="home-hero-clock">
      <path d="M 30.01 14.10 A 19 19 0 1 1 14.15 39.50" fill="none"
            stroke="var(--text)" strokeWidth="4.4" strokeLinecap="round" />
      <line x1="10.39" y1="40.87" x2="6.16"  y2="42.41" stroke="#bbf7d0" strokeWidth="3.4" strokeLinecap="round" />
      <line x1="9.01"  y1="33.80" x2="4.52"  y2="33.96" stroke="#86efac" strokeWidth="3.4" strokeLinecap="round" />
      <line x1="9.89"  y1="26.66" x2="5.57"  y2="25.42" stroke="#4ade80" strokeWidth="3.4" strokeLinecap="round" />
      <line x1="12.93" y1="20.14" x2="9.20"  y2="17.62" stroke="#34d399" strokeWidth="3.4" strokeLinecap="round" />
      <line x1="17.84" y1="14.88" x2="15.07" y2="11.33" stroke="#22c55e" strokeWidth="3.4" strokeLinecap="round" />
      <line x1="24.13" y1="11.39" x2="22.59" y2="7.16"  stroke="#16a34a" strokeWidth="3.4" strokeLinecap="round" />
      <line x1="32" y1="33" x2="32"    y2="21"    stroke="var(--text)" strokeWidth="3.2" strokeLinecap="round" />
      <line x1="32" y1="33" x2="44.29" y2="41.60" stroke="var(--text)" strokeWidth="3.2" strokeLinecap="round" />
      <circle cx="32" cy="33" r="3.1" fill="#22c55e" />
    </svg>
  );
}

const CardIcon = {
  accounts: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 7v5l3.5 2.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  premium: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3l2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 22.4V3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M12 3L9.4 8.4l-5.9.8 4.3 4.1-1 5.9L12 16.5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  ),
  bell: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 9a6 6 0 1112 0c0 4 1.2 5.6 2 6.4H4c.8-.8 2-2.4 2-6.4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M10 19a2 2 0 004 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  settings: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 2.5v2.2M12 19.3v2.2M4.2 4.2l1.6 1.6M18.2 18.2l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.2 19.8l1.6-1.6M18.2 5.8l1.6-1.6"
            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
};

export default function Home() {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const cards = [
    {
      key: 'accounts',
      icon: CardIcon.accounts,
      title: 'Zeitkonten',
      text: 'Der Saldo jeder Mitarbeiterin und jedes Mitarbeiters auf einen Blick, inklusive Ampel, Wochenarbeitszeit, Krankheitstagen und geplantem Urlaub. Eine Zeile anklicken zeigt den Verlauf der letzten Monate.',
      action: () => navigate('/dashboard'),
      cta: 'Zu den Zeitkonten',
    },
    {
      key: 'premium',
      icon: CardIcon.premium,
      title: 'Zeitzuschläge',
      text: 'Automatisch erkannte Zuschläge für Nacht-, Sonntags-, Feiertags-, Heiligabend/Silvester- und Samstagsarbeit. Ausstehende Zuschläge lassen sich hier prüfen und zur Buchung freigeben.',
      action: () => navigate('/premium'),
      cta: 'Zu den Zeitzuschlägen',
    },
    {
      key: 'notifications',
      icon: CardIcon.bell,
      title: 'Benachrichtigungen',
      text: 'KimaiAssist verschickt monatliche Berichte und Hinweise per E-Mail, etwa bei roter Ampel, offenem Resturlaub im November oder ungebuchten Zeitzuschlägen. Betroffene Mitarbeitende erhalten auf Wunsch eine persönliche Nachricht.',
      action: isSuperAdmin ? () => navigate('/settings') : null,
      cta: 'In den Einstellungen anpassen',
    },
  ];

  if (isSuperAdmin) {
    cards.push({
      key: 'settings',
      icon: CardIcon.settings,
      title: 'Einstellungen',
      text: 'Als Administrator legst du hier die Zuschlagsraten, die Ampel-Grenzwerte, die Empfänger und Auslöser der Benachrichtigungen fest. Jede Mail lässt sich vorab an eine Debug-Adresse testen.',
      action: () => navigate('/settings'),
      cta: 'Zu den Einstellungen',
    });
  }

  const steps = [
    {
      title: 'Zeitkonten prüfen',
      text: 'Öffne die Zeitkonten und verschaffe dir über die Ampel einen Überblick. Grün bedeutet im Rahmen, Orange eine erste Abweichung, Rot eine dauerhafte Über- oder Unterschreitung.',
    },
    {
      title: 'Verlauf ansehen',
      text: 'Klicke eine Person an, um den Verlauf der monatlichen Snapshots mit Saldo, Krankheitstagen und Resturlaub zu sehen.',
    },
    {
      title: 'Zeitzuschläge freigeben',
      text: 'Wechsle zu den Zeitzuschlägen, kontrolliere die automatisch erkannten Zuschläge und gib die korrekten Einträge zur Buchung frei.',
    },
    isSuperAdmin
      ? {
          title: 'Benachrichtigungen einrichten',
          text: 'Hinterlege in den Einstellungen die Empfänger und aktiviere die gewünschten Auslöser. Teste jede Mail zunächst an die Debug-Adresse, bevor echte Empfänger sie erhalten.',
        }
      : {
          title: 'Benachrichtigungen erhalten',
          text: 'Liegt dein Zeitkonto außerhalb des grünen Bereichs, informiert dich KimaiAssist nach dem monatlichen Stichtag automatisch per E-Mail.',
        },
  ];

  return (
    <div className="dash-root">
      <Sidebar />

      <main className="dash-main home-main">
        <section className="home-hero">
          <HeroClock />
          <div className="home-hero-text">
            <p className="home-eyebrow">{user ? `Willkommen zurück, ${user}` : 'Willkommen'}</p>
            <h1 className="home-title">Kimai<strong>Assist</strong></h1>
            <p className="home-lead">
              KimaiAssist ergänzt eure Kimai-Zeiterfassung um eine Auswertung der Zeitkonten,
              die automatische Erkennung von Zeitzuschlägen und E-Mail-Benachrichtigungen.
              Alle Daten werden dabei nur lesend aus Kimai übernommen.
            </p>
          </div>
        </section>

        <section className="home-section">
          <h2 className="home-section-title">Was KimaiAssist für dich tut</h2>
          <div className="home-cards">
            {cards.map(card => (
              <div
                key={card.key}
                className={`home-card${card.action ? ' home-card--clickable' : ''}`}
                onClick={card.action || undefined}
                role={card.action ? 'button' : undefined}
                tabIndex={card.action ? 0 : undefined}
                onKeyDown={card.action ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); card.action(); } } : undefined}
              >
                <span className="home-card-icon">{card.icon}</span>
                <h3 className="home-card-title">{card.title}</h3>
                <p className="home-card-text">{card.text}</p>
                {card.action && <span className="home-card-cta">{card.cta} &rarr;</span>}
              </div>
            ))}
          </div>
        </section>

        <section className="home-section">
          <h2 className="home-section-title">So nutzt du KimaiAssist</h2>
          <ol className="home-steps">
            {steps.map((step, i) => (
              <li key={i} className="home-step">
                <span className="home-step-number">{i + 1}</span>
                <div className="home-step-body">
                  <h3 className="home-step-title">{step.title}</h3>
                  <p className="home-step-text">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <p className="home-footnote">
          Tipp: Über die Navigation links wechselst du jederzeit zwischen den Bereichen.
        </p>
      </main>
    </div>
  );
}
